import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuthContext } from "./MsalProvider";
import {
  BffAuthError,
  bffLogout,
  clearCsrfToken,
  loadCsrfToken,
  purgeLegacyIdpCaches,
  refreshBffTokens,
  storeCsrfToken,
} from "./bff/bffClient";
import { decodeJwtExpiryMs, emailFromJwt } from "./jwt";
import { recordStudioLogin } from "../services/studio/api";
import { invalidateCommandCache } from "../chat/slashCommands/registry";
import {
  bindIdentity,
  logError,
  logEvent,
  scrubMessage,
  setTelemetryAuthToken,
  setTelemetryTokenRefresher,
} from "../telemetry";

/* ── Token persistence ───────────────────────── */

/**
 * AIVA-6685 (V-08 — auth tokens in browser storage): the Graph / ID / API
 * *access* tokens (and the expiry / activity bookkeeping around them) are
 * persisted in `sessionStorage`, NOT `localStorage`, so they are cleared when
 * the taskpane window closes and are not left in persistent storage for a
 * same-origin XSS to exfiltrate. This mirrors the Studio-app fix.
 *
 * Trade-off: a persisted session is no longer restored across a pane *reopen*
 * (a fresh taskpane window starts with empty sessionStorage) — instead the
 * mount-time cookie refresh, or failing that the auth dialog, re-acquires
 * tokens. These are short-lived access tokens the backend can always re-mint,
 * so nothing durable is lost.
 *
 * The long-lived *refresh* token is no longer in the browser at all: it is
 * sealed in an HttpOnly cookie the backend sets, which is what closes V-08.
 * The only auth value left in `localStorage` is the CSRF secret paired with
 * that cookie (see bff/bffClient) — not a credential, and useless without it.
 *
 * Consequence: the cross-window `storage` event used for cross-tab sign-in/out
 * sync (see the effect below) no longer fires for these keys, which is fine —
 * an add-in taskpane is effectively single-window.
 */
const tokenStore: Pick<Storage, "getItem" | "setItem" | "removeItem"> =
  globalThis.sessionStorage;

const GRAPH_TOKEN_KEY = "aiva_graph_token";
const GRAPH_TOKEN_TS_KEY = "aiva_graph_token_ts";
const ID_TOKEN_KEY = "aiva_id_token";
const ID_TOKEN_TS_KEY = "aiva_id_token_ts";
const API_TOKEN_KEY = "aiva_api_token";
const API_TOKEN_TS_KEY = "aiva_api_token_ts";
/**
 * The soonest *real* token expiry (epoch ms, as reported by the IdP), persisted
 * so a session rehydrated from sessionStorage schedules its proactive refresh
 * against the true expiry instead of the 50-min approximation in `loadInitialExpiry`.
 */
const TOKEN_EXPIRY_KEY = "aiva_token_expiry";
/**
 * Cadence of the heartbeat backstop. A single `setTimeout` (and the
 * focus/visibility events) are unreliable inside Office webviews — throttled or
 * suspended while the host app is backgrounded/asleep, and focus events often
 * never fire in embedded WebViews. This interval re-evaluates expiry repeatedly
 * so the refresh fires as soon as the webview gets any execution time again.
 */
const HEARTBEAT_INTERVAL_MS = 60 * 1000;
/**
 * Hard cap on how long we wait for the auth dialog to post a result back. A
 * dialog that never messages (closed, blocked, or "dialog already open") would
 * otherwise leave the refresh promise pending forever and pin `refreshInFlightRef`,
 * wedging every future refresh. On timeout we resolve null and close the dialog.
 */
const DIALOG_TIMEOUT_MS = 60 * 1000;
/**
 * Approximate token lifetime used to schedule the proactive refresh of a
 * session rehydrated from sessionStorage (Graph tokens expire at 60–90 min). Kept
 * conservative so the proactive refresh arms early.
 */
const TOKEN_MAX_AGE_MS = 50 * 60 * 1000;
/**
 * How long a persisted token is allowed to be restored on reload. Decoupled
 * from TOKEN_MAX_AGE_MS: a restored token may already be past its real Graph
 * expiry, but as long as the sealed refresh token in the session cookie is
 * plausibly alive (SPA refresh tokens last ~24h) we keep the user signed in and
 * let the refresh paths mint a fresh access token, instead of dumping them to
 * the sign-in screen. Beyond this window the session is treated as truly dead.
 */
const TOKEN_RESTORE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
/**
 * AIVA-5596: idle-session timeout. After this long with no genuine user
 * interaction the session is treated as dead — the persisted tokens are not
 * restored on reopen and a live session is silently signed out. This is
 * deliberately decoupled from token age: the proactive timer, focus/visibility
 * recovery and the heartbeat all re-mint tokens with zero user involvement, so
 * without this gate an idle pane stays signed in for the full 24h restore
 * window. The stamp it checks (`LAST_ACTIVITY_KEY`) is advanced ONLY by real
 * user activity, never by a token refresh.
 *
 * The window itself is the Session Persistence setting on the Studio SSO page,
 * delivered with the pre-sign-in discovery config (`useAuthContext`), so it is
 * known before the reopen gate runs. This is the window when none is set.
 */
const DEFAULT_INACTIVITY_TIMEOUT_MS = 2 * 60 * 60 * 1000;
/**
 * How long before the inactivity deadline the "your session is about to expire"
 * prompt is raised. The timeout used to fire with no notice at all: a pane left
 * open came back to the sign-in screen with no explanation and no chance to keep
 * working, which is what this lead time exists to fix — long enough for someone
 * who stepped away to come back and answer, short enough that the prompt is
 * clearly about the session they are looking at.
 */
const INACTIVITY_WARNING_LEAD_MS = 5 * 60 * 1000;
/** Epoch ms of the last genuine user interaction with the task pane. */
const LAST_ACTIVITY_KEY = "aiva_last_activity";
/**
 * Cap how often the activity stamp is written to sessionStorage. Activity events
 * (mousemove/keydown/click) fire far too often to persist each one; once every
 * 30s keeps the stamp fresh enough for a timeout measured in minutes without
 * hammering storage.
 */
const ACTIVITY_WRITE_THROTTLE_MS = 30 * 1000;
/** Refresh proactively this far before the token's real expiry. */
const REFRESH_BUFFER_MS = 5 * 60 * 1000;
/**
 * AIVA-5972: how long a just-minted token is reused before a fresh refresh is
 * attempted. After idle a stale token triggers reactive 401s at several layers
 * in quick succession (the per-request access-guard preflight,
 * fetchWithRefresh's own 401 retry, and the chat outer retry), each of which
 * would otherwise fire its own refresh. Reusing the token minted by the first
 * one for this window collapses that burst into a single round-trip. A minted
 * token is valid ~60 min, so returning it for a few seconds is always safe —
 * the only tokens that reach this cache are freshly acquired (never the
 * possibly-stale ones rehydrated from sessionStorage, which do not flow through
 * applyTokens).
 *
 * This mattered more when every refresh opened an Office dialog window; the
 * refresh is an invisible fetch now, so the window is a round-trip saving
 * rather than a way to stop popups stacking.
 */
const REFRESH_COALESCE_WINDOW_MS = 30 * 1000;

function persistToken(key: string, tsKey: string, token: string | null) {
  try {
    if (token) {
      tokenStore.setItem(key, token);
      tokenStore.setItem(tsKey, String(Date.now()));
    }
  } catch { /* storage unavailable */ }
}

function persistTokens(graphToken: string, idToken: string | null, apiToken: string | null) {
  persistToken(GRAPH_TOKEN_KEY, GRAPH_TOKEN_TS_KEY, graphToken);
  persistToken(ID_TOKEN_KEY, ID_TOKEN_TS_KEY, idToken);
  persistToken(API_TOKEN_KEY, API_TOKEN_TS_KEY, apiToken);
}

/** Persist the real token expiry (epoch ms), or clear it when unknown. */
function persistExpiry(expiresAt: number | null) {
  try {
    if (expiresAt == null) {
      tokenStore.removeItem(TOKEN_EXPIRY_KEY);
    } else {
      tokenStore.setItem(TOKEN_EXPIRY_KEY, String(expiresAt));
    }
  } catch { /* storage unavailable */ }
}

/** The full set of keys this provider manages, in one place. */
const MANAGED_TOKEN_KEYS = [
  GRAPH_TOKEN_KEY, GRAPH_TOKEN_TS_KEY,
  ID_TOKEN_KEY, ID_TOKEN_TS_KEY,
  API_TOKEN_KEY, API_TOKEN_TS_KEY,
  TOKEN_EXPIRY_KEY,
  LAST_ACTIVITY_KEY,
] as const;

function clearPersistedTokens() {
  try {
    for (const k of MANAGED_TOKEN_KEYS) {
      tokenStore.removeItem(k);
    }
  } catch { /* storage unavailable */ }
}

/**
 * AIVA-6685 (V-08): one-time migration. Before this fix these tokens were
 * persisted in `localStorage`; an already-signed-in user carries them there
 * after the upgrade. Switching the store to sessionStorage stops *writing* to
 * localStorage but would leave the flagged tokens orphaned in it indefinitely,
 * exactly where the vulnerability says they must not be. Purge any legacy
 * copies from localStorage on startup so the fix removes existing exposure, not
 * just future writes. Safe no-op once localStorage holds none of these keys.
 */
function purgeLegacyLocalStorageTokens() {
  try {
    for (const k of MANAGED_TOKEN_KEYS) {
      localStorage.removeItem(k);
    }
  } catch { /* storage unavailable */ }
}

/** Stamp "now" as the last user-activity time (AIVA-5596). */
function recordActivity() {
  try {
    tokenStore.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  } catch { /* storage unavailable */ }
}

/** Last user-activity time (epoch ms), or null when absent/unparseable. */
function loadLastActivity(): number | null {
  try {
    const raw = tokenStore.getItem(LAST_ACTIVITY_KEY);
    if (raw == null) return null;
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch { /* storage unavailable */ }
  return null;
}

/**
 * The idle window to enforce, in ms, for the Studio setting `minutes` (null when
 * unset). A window at or below the warning lead would reopen the prompt on every
 * heartbeat; Studio rejects one on save, so this only guards a malformed payload.
 */
function inactivityTimeoutMs(minutes: number | null | undefined): number {
  const ms = (minutes ?? 0) * 60 * 1000;
  return Number.isInteger(minutes) && ms > INACTIVITY_WARNING_LEAD_MS ? ms : DEFAULT_INACTIVITY_TIMEOUT_MS;
}

/**
 * Epoch ms at which the inactivity timeout will fire, or null when no activity
 * stamp is readable. Null is deliberately *not* folded into a deadline: a
 * missing stamp is not a measurable idle period, so the countdown has nothing to
 * count down to — the callers that need a real deadline skip it, while the gates
 * that must fail closed (below) still treat it as expired.
 */
function idleDeadlineAt(timeoutMs: number): number | null {
  const lastActivity = loadLastActivity();
  return lastActivity == null ? null : lastActivity + timeoutMs;
}

/**
 * Whether the session has crossed the inactivity timeout (AIVA-5596). A missing
 * stamp counts as expired so a session persisted before this feature shipped (or
 * with the stamp cleared) forces one clean re-login instead of being trusted.
 */
function isInactivityExpired(timeoutMs: number): boolean {
  const deadline = idleDeadlineAt(timeoutMs);
  if (deadline == null) return true;
  return Date.now() > deadline;
}

function loadPersistedToken(key: string, tsKey: string, idleTimeoutMs: number): string | null {
  // AIVA-5596: an idle reopen must land on the sign-in screen. If the session
  // crossed the inactivity timeout, drop the whole persisted set so none of the
  // three loaders restores a stale token (this runs before any refresh effect).
  if (isInactivityExpired(idleTimeoutMs)) {
    clearPersistedTokens();
    return null;
  }
  try {
    const token = tokenStore.getItem(key);
    const ts = tokenStore.getItem(tsKey);
    if (token && ts && Date.now() - Number(ts) < TOKEN_RESTORE_MAX_AGE_MS) {
      return token;
    }
    tokenStore.removeItem(key);
    tokenStore.removeItem(tsKey);
  } catch { /* storage unavailable */ }
  return null;
}

function loadPersistedGraphToken(idleTimeoutMs: number): string | null {
  return loadPersistedToken(GRAPH_TOKEN_KEY, GRAPH_TOKEN_TS_KEY, idleTimeoutMs);
}

function loadPersistedIdToken(idleTimeoutMs: number): string | null {
  return loadPersistedToken(ID_TOKEN_KEY, ID_TOKEN_TS_KEY, idleTimeoutMs);
}

function loadPersistedApiToken(idleTimeoutMs: number): string | null {
  return loadPersistedToken(API_TOKEN_KEY, API_TOKEN_TS_KEY, idleTimeoutMs);
}

/**
 * Initial expiry for a token rehydrated from sessionStorage. Prefers the real
 * expiry persisted by `applyTokens`; falls back to approximating from
 * the stored timestamp + max-age for sessions persisted before the real expiry
 * was tracked. Lets the proactive refresh / heartbeat arm immediately on a
 * restored session instead of waiting for the first reactive refresh.
 */
function loadInitialExpiry(): number | null {
  try {
    const real = tokenStore.getItem(TOKEN_EXPIRY_KEY);
    if (real) return Number(real);
    const ts = tokenStore.getItem(GRAPH_TOKEN_TS_KEY);
    if (ts) return Number(ts) + TOKEN_MAX_AGE_MS;
  } catch { /* storage unavailable */ }
  return null;
}

/* ── Context ─────────────────────────────────── */

interface AuthState {
  /** Microsoft Graph access token — used for Graph API calls (mail, calendar, files). */
  graphToken: string | null;
  /** OIDC ID token — used as the bearer for our own .NET API. */
  idToken: string | null;
  /**
   * Access token whose audience is the AIVA backend app reg. APIM validates
   * this token; the FastAPI service either uses its claims directly or
   * performs an OBO exchange when it needs to call Graph itself.
   * Falls back to the Graph token when no API scope is configured so dev
   * environments without an API app-reg keep working against a backend
   * that has the legacy unverified Graph-token fallback enabled.
   */
  apiToken: string | null;
  signedIn: boolean;
  ready: boolean;
  signIn: () => void;
  signOut: () => void;
  /**
   * Silently re-acquire tokens by redeeming the session cookie at
   * /api/auth/refresh, and update state. Resolves to the fresh backend token
   * (api ?? graph) so a caller retrying a 401'd request can use it
   * immediately, or null when refresh was not possible (dead session /
   * transient failure). Concurrent callers share a single in-flight refresh.
   *
   * `foreground` (default true) marks a caller the user is waiting on, and
   * decides one thing only: whether a dead session signs the user out. It no
   * longer gates a dialog — refresh is an invisible fetch for every caller —
   * but a background timer must never eject someone mid-task on one miss, so
   * the proactive timer, focus recovery, heartbeat and telemetry pass false.
   */
  refreshTokens: (foreground?: boolean) => Promise<string | null>;
  /**
   * Like `refreshTokens`, but resolves to the fresh **OIDC id token** instead
   * of the backend access token. Used by callers whose bearer is the id token
   * (the addin-api Studio settings / connector-credentials calls) so a 401
   * retry re-sends a refreshed token of the *same audience*, not an access
   * token. Shares the same single in-flight refresh as `refreshTokens`.
   */
  refreshIdToken: (foreground?: boolean) => Promise<string | null>;
  /**
   * Like `refreshTokens`, but resolves to the fresh **Microsoft Graph** access
   * token. Used by direct Graph calls (email / calendar / files) so a 401 retry
   * re-sends a refreshed Graph token rather than the API/id token. Shares the
   * same single in-flight refresh as `refreshTokens`.
   */
  refreshGraphToken: (foreground?: boolean) => Promise<string | null>;
  /**
   * Pre-flight used by user-initiated backend calls (chat send): resolves to a
   * backend bearer that is fresh past the refresh buffer, refreshing FIRST when
   * the tracked expiry says the current token is stale, so we never send a token
   * we already know is expired. Resolves null only when signed out.
   */
  ensureFreshToken: () => Promise<string | null>;
  /**
   * Epoch ms at which this session will be dropped for inactivity — set only
   * once the session is inside the warning window, null the rest of the time
   * (so `idleWarningDeadline != null` *is* "the prompt should be showing").
   *
   * The deadline is published rather than a live countdown so the auth context
   * changes identity twice per idle cycle instead of once a second: the prompt
   * ticks its own display off this fixed value.
   */
  idleWarningDeadline: number | null;
  /**
   * Answer the idle prompt with "keep me signed in": restarts the inactivity
   * clock, closes the prompt, and silently re-mints the tokens the session
   * idled on.
   */
  extendSession: () => void;
  /**
   * End the session because the idle countdown ran out. Called by the prompt
   * when the countdown it is showing reaches zero, so the sign-out lands on the
   * second the user was watching for instead of on the next 60s heartbeat. The
   * heartbeat's own idle check remains the backstop, so the timeout still fires
   * when no prompt is mounted.
   */
  endIdleSession: () => void;
  /**
   * True when the session that just ended was ended by the inactivity timeout
   * rather than by the user. Lets the sign-in page say why it is showing.
   * Cleared as soon as tokens are acquired again.
   */
  signedOutForInactivity: boolean;
  /** The idle window this session runs on, in minutes; the sign-in page states it. */
  idleTimeoutMinutes: number;
}

const AuthStateCtx = createContext<AuthState>({
  graphToken: null,
  idToken: null,
  apiToken: null,
  signedIn: false,
  ready: false,
  signIn: () => {},
  signOut: () => {},
  refreshTokens: () => Promise.resolve(null),
  refreshIdToken: () => Promise.resolve(null),
  refreshGraphToken: () => Promise.resolve(null),
  ensureFreshToken: () => Promise.resolve(null),
  idleWarningDeadline: null,
  extendSession: () => {},
  endIdleSession: () => {},
  signedOutForInactivity: false,
  idleTimeoutMinutes: DEFAULT_INACTIVITY_TIMEOUT_MS / (60 * 1000),
});

export function useAuthState(): AuthState {
  return useContext(AuthStateCtx);
}

/* ── Dialog-based sign-in / refresh for hosts without a taskpane account ── */

/** Payload posted back from auth-dialog.html via messageParent(). */
interface DialogTokenPayload {
  status: string;
  accessToken?: string;
  idToken?: string;
  apiToken?: string;
  /** Soonest real token expiry, epoch ms. */
  expiresOn?: number;
  /** BFF flow only: the CSRF secret paired with the session cookie. */
  csrfToken?: string;
  message?: string;
}

/**
 * Result of a silent refresh — the cookie-credentialed fetch, not a dialog.
 * - bundle → fresh tokens to apply
 * - "interaction_required" → the session cookie is missing or dead (sign out)
 * - null → transient failure (keep the session)
 */
type RefreshResult =
  | { accessToken: string; idToken: string | null; apiToken: string | null; expiresAt: number | null }
  | "interaction_required"
  | null;

/**
 * Open auth-dialog.html and invoke `onMessage` once with the parsed payload,
 * or null if the dialog could not be opened or returned an unparseable message.
 *
 * Outlook runs the add-in inside a broker iframe (brk-*:// scheme) and desktop
 * Word/PowerPoint inside an embedded WebView — both block acquireTokenPopup(),
 * so the Office Dialog API (a separate window) is the only reliable approach.
 * `onMessage` is invoked synchronously inside the dialog event handler so React
 * state updates flush within the caller's act()/event tick.
 */
function runAuthDialog(onMessage: (data: DialogTokenPayload | null) => void) {
  // Interactive sign-in is the only thing that opens this dialog: silent
  // refresh is a cookie-credentialed fetch that never shows a window, so the
  // page takes no mode parameter and the window is always sized for the IdP's
  // login UI.
  const dialogUrl = `${globalThis.location.origin}/auth-dialog.html`;
  Office.context.ui.displayDialogAsync(
    dialogUrl,
    { height: 60, width: 30 },
    (asyncResult) => {
      if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
        console.error("[AuthState] Failed to open auth dialog:", asyncResult.error?.message);
        logError("app.auth.failure", {
          type: "DialogOpenError",
          scrubbedMessage: scrubMessage(asyncResult.error?.message ?? "dialog_open_failed"),
        }, { reason: "dialog_open_failed" });
        onMessage(null);
        return;
      }
      const dialog = asyncResult.value;
      let settled = false;
      // Holder so `settle` (defined first) can clear the timer assigned just below.
      const timing: { timer?: ReturnType<typeof setTimeout> } = {};
      // Settle exactly once — deliver the payload (or null), stop the timeout, and
      // close the dialog. Every exit path (message, parse failure, timeout, the
      // dialog being closed) funnels through here so `runAuthDialog`'s caller is
      // never left waiting on a dialog that won't post back.
      const settle = (data: DialogTokenPayload | null) => {
        if (settled) return;
        settled = true;
        if (timing.timer) clearTimeout(timing.timer);
        try { dialog.close(); } catch { /* already closed */ }
        onMessage(data);
      };
      timing.timer = setTimeout(() => {
        logError("app.auth.failure", {
          type: "DialogTimeout",
          scrubbedMessage: "dialog_timed_out",
        }, { reason: "dialog_timed_out" });
        settle(null);
      }, DIALOG_TIMEOUT_MS);
      dialog.addEventHandler(
        Office.EventType.DialogMessageReceived,
        (arg: unknown) => {
          try {
            const { message } = arg as { message: string };
            settle(JSON.parse(message) as DialogTokenPayload);
          } catch (err) {
            logError("app.auth.failure", {
              type: "DialogParseError",
              scrubbedMessage: scrubMessage(err instanceof Error ? err.message : String(err)),
            }, { reason: "dialog_message_parse_failed" });
            settle(null);
          }
        },
      );
      // Dialog dismissed by the user or closed by the host (e.g. event 12006) —
      // resolve so the refresh doesn't hang waiting for a message that never comes.
      dialog.addEventHandler(Office.EventType.DialogEventReceived, () => settle(null));
    },
  );
}

/**
 * Interactive sign-in: opens the dialog and hands fresh tokens to `onToken`. The
 * real `expiresOn` is threaded through so the proactive-refresh timer arms off the
 * token's true expiry, not an approximation. The dialog's closing `focus` event
 * can't loop a spurious refresh here: background triggers never open the dialog
 * (see the proactive/focus effects), so no cooldown is needed.
 */
function signInViaDialog(
  onToken: (
    accessToken: string,
    idToken: string | null,
    apiToken: string | null,
    expiresAt: number | null,
  ) => void,
) {
  runAuthDialog((data) => {
    if (!data) return;
    if (data.status === "success" && data.accessToken) {
      // BFF flow: the dialog's code exchange also minted the CSRF secret the
      // taskpane needs for every /api/auth/refresh; keep it before the tokens
      // are applied so the first proactive refresh can already authenticate.
      if (data.csrfToken) storeCsrfToken(data.csrfToken);
      onToken(data.accessToken, data.idToken ?? null, data.apiToken ?? null, data.expiresOn ?? null);
    } else {
      logError("app.auth.failure", {
        type: "DialogMessageError",
        scrubbedMessage: scrubMessage(data.status || "dialog_returned_non_success"),
      }, { reason: "dialog_returned_non_success" });
    }
  });
}

/**
 * Silent refresh. The encrypted HttpOnly cookie (sent by the browser, never
 * readable here) carries the refresh token; the CSRF secret proves the call
 * originated from this app. Completely invisible — no window, no flash — so
 * background triggers can use it as freely as user actions. This replaced a
 * hidden-dialog refresh that spent a browser-held refresh token (V-08).
 */
async function refreshViaBff(
  channel: string,
  accessToken: string | null,
  staleGraphToken: string | null = null,
): Promise<RefreshResult> {
  const csrf = loadCsrfToken();
  if (!csrf) {
    // No CSRF secret means no completed BFF sign-in to renew (or storage was
    // cleared) — the session cannot be refreshed, only re-established.
    return "interaction_required";
  }
  try {
    // The current API-audience token rides along as the bearer: the gateway
    // validates it on this route like any other, so a refresh that fires after
    // the token has already lapsed is rejected at the edge. The proactive
    // timer and heartbeat exist to keep that from happening.
    const tokens = await refreshBffTokens(channel, csrf, accessToken);
    storeCsrfToken(tokens.csrfToken);
    return {
      // Same audience contract as the dialog payload: `accessToken` is the
      // Graph-capable slot. Off-Entra the API token doubles for it (Graph
      // features are gated off anyway). On Entra the backend's Graph leg is
      // best-effort — when it fails, prefer the caller-supplied PREVIOUS
      // Graph token (right audience, merely older) over the API-audience
      // token, which Graph would 401 in a loop for the coalesce window.
      accessToken: tokens.graphToken ?? staleGraphToken ?? tokens.accessToken,
      idToken: tokens.idToken,
      apiToken: tokens.accessToken,
      // 0 means the IdP omitted expires_in — treat as unknown so applyTokens
      // falls back to its conservative default instead of "expired at mint",
      // which would put the heartbeat into a permanent refresh loop.
      expiresAt: tokens.expiresIn > 0 ? Date.now() + tokens.expiresIn * 1000 : null,
    };
  } catch (err) {
    if (err instanceof BffAuthError && err.status === 401) {
      // The cookie is missing/expired, the CSRF pair failed, or the IdP
      // revoked the refresh token — the session is dead.
      return "interaction_required";
    }
    logError("app.auth.failure", {
      type: "BffRefreshError",
      scrubbedMessage: scrubMessage(err instanceof Error ? err.message : String(err)),
    }, { reason: "bff_refresh_failed" });
    return null;
  }
}

/* ── Provider ─────────────────────────────────── */

interface Props {
  readonly children: ReactNode;
}

export function AuthStateProvider({ children }: Readonly<Props>) {
  const { ready: authReady, channel, capabilities, idleTimeoutMinutes: configuredIdleMinutes } = useAuthContext();
  // Fixed for the life of the pane: the bootstrap resolves it once, before this
  // provider mounts, so a Studio change applies the next time the add-in opens.
  const idleTimeoutMs = inactivityTimeoutMs(configuredIdleMinutes);
  const bffChannel = channel ?? "outlook";
  const [graphToken, setGraphToken] = useState<string | null>(() => loadPersistedGraphToken(idleTimeoutMs));
  const [idToken, setIdToken] = useState<string | null>(() => loadPersistedIdToken(idleTimeoutMs));
  const [apiToken, setApiToken] = useState<string | null>(() => loadPersistedApiToken(idleTimeoutMs));

  // Idle-expiry prompt: the epoch ms this session lapses at, non-null only while
  // the session is inside the warning window (see `idleWarningDeadline` on the
  // context for why a deadline is published instead of a countdown).
  const [idleWarningDeadline, setIdleWarningDeadline] = useState<number | null>(null);
  // Mirrors the above for the activity recorder and the heartbeat, which both
  // need "is the prompt up?" from inside a callback that must not re-subscribe
  // its listeners (the activity effect) every time the answer flips.
  const idleWarningRef = useRef(false);
  // Whether the last sign-out was the inactivity timeout firing rather than the
  // user asking. Drives the explanation on the sign-in page.
  const [signedOutForInactivity, setSignedOutForInactivity] = useState(false);

  // Soonest token expiry (ms epoch), used to schedule the proactive refresh.
  // Approximated from the persisted timestamp on a restored session; replaced
  // with the IdP's real expiry the first time `applyTokens` runs.
  const expiresAtRef = useRef<number | null>(loadInitialExpiry());
  // Dedupes concurrent refreshes onto one round-trip (mirrors the connector
  // adapter's `refreshInFlight`).
  const refreshInFlightRef = useRef<Promise<string | null> | null>(null);
  // Whether the in-flight refresh was started by a foreground caller. A
  // background refresh (proactive timer / focus / heartbeat) can resolve to
  // null on a dead session WITHOUT signing out, by design. A reactive 401 retry
  // must not be deduped onto such a refresh — otherwise it inherits that null
  // and shows "Your sign-in has expired." mid-session instead of either
  // recovering or signing out cleanly, which is the false expiry the user hits
  // when a prompt races a background refresh (AIVA-5861).
  const refreshInFlightForegroundRef = useRef<boolean>(false);
  // Latest id token, mirrored synchronously inside `applyTokens` so
  // `refreshIdToken` can read the freshly-minted value the instant the shared
  // refresh resolves (React state / a post-render effect would be too late).
  const lastIdTokenRef = useRef<string | null>(idToken);
  // Same idea for the Graph access token, read by `refreshGraphToken`.
  const lastGraphTokenRef = useRef<string | null>(graphToken);
  // Same idea for the backend (api) access token, so `ensureFreshToken` can hand
  // back the current backend bearer synchronously (no React state read).
  const lastApiTokenRef = useRef<string | null>(apiToken);
  // AIVA-5972 refresh-coalescing cache: the backend token (api ?? graph) minted
  // by the most recent refresh (refreshSession), plus when it landed.
  // A refresh requested within REFRESH_COALESCE_WINDOW_MS reuses this instead of
  // making a second round-trip. Seeded by every successful refresh, and cleared
  // on signOut so a post-sign-out call can't hand out a dead token.
  const lastRefreshTokenRef = useRef<string | null>(null);
  const lastRefreshAtRef = useRef<number>(0);
  // AIVA-6131 / AIVA-5861: the single in-flight refresh promise. Serves
  // two purposes at once:
  //   1. Single-dialog invariant — while a refresh dialog is open this ref is
  //      non-null, so a concurrent refresh can never stack a second popup,
  //      independent of any timing window.
  //   2. Shared result — a concurrent caller (e.g. the chat api-token refresh
  //      racing the Graph-token refresh; the two use DIFFERENT-audience
  //      refreshers that both land here) AWAITS this promise and receives the
  //      freshly-minted token, instead of the not-yet-populated
  //      lastRefreshTokenRef (null). That stale null was inherited by the
  //      request that raced the in-flight refresh and surfaced as a false
  //      "Your sign-in has expired" chat 401 — even though the very same
  //      refresh was about to succeed (AIVA-5861).
  const sessionRefreshInFlightRef = useRef<Promise<string | null> | null>(null);

  const signedIn = graphToken !== null;
  // Synchronous mirror of `signedIn` for the refresh path. `signedIn` is derived
  // from React state, so a callback closure (or a queued timer / in-flight 401
  // retry) can read a stale value after signOut. This ref is flipped the instant
  // tokens are applied (true) or cleared (false), so `doRefresh` can hard-stop
  // any refresh the moment we're signed out.
  const signedInRef = useRef<boolean>(signedIn);

  /** Set tokens in state and persist to sessionStorage. */
  const applyTokens = useCallback(
    (
      accessToken: string,
      newIdToken: string | null,
      newApiToken: string | null,
      expiresAt?: number | null,
    ) => {
      persistTokens(accessToken, newIdToken, newApiToken);
      // Schedule the proactive refresh against the SOONEST of the access-token
      // expiry (the IdP's `expires_in`, passed in) and the idToken's own `exp`. Graph
      // access tokens live 60–90 min but the OIDC idToken ~60 min, so the idToken
      // usually dies first; tracking only the access-token expiry let the idToken
      // lapse and 401'd the idToken-bearing addin-api + telemetry calls.
      const accessExpiry = expiresAt ?? Date.now() + TOKEN_MAX_AGE_MS;
      const idExpiry = decodeJwtExpiryMs(newIdToken);
      const nextExpiry = idExpiry == null ? accessExpiry : Math.min(accessExpiry, idExpiry);
      expiresAtRef.current = nextExpiry;
      persistExpiry(nextExpiry);
      signedInRef.current = true;
      setGraphToken(accessToken);
      lastGraphTokenRef.current = accessToken;
      if (newIdToken) {
        setIdToken(newIdToken);
        lastIdTokenRef.current = newIdToken;
      }
      if (newApiToken) {
        setApiToken(newApiToken);
        lastApiTokenRef.current = newApiToken;
      }
      // Stamp the Studio Logs page's User column for add-in telemetry rows
      // (AIVA-7822). The API access token is stripped of email/preferred_username
      // and the Telemetry ingest service has no user database, so the server
      // can't resolve it — but the id token still carries it for every IdP.
      // Bind it here so subsequent events attribute to the real user; the
      // server sanitises the value and keeps the bearer's oid as the trust
      // anchor. Falls back to the access token when the id token lacks the claim.
      const userEmail = emailFromJwt(newIdToken) ?? emailFromJwt(accessToken);
      if (userEmail) {
        bindIdentity({ userEmail });
      }
      logEvent("app.auth.success", "info");
    },
    [],
  );

  // Whether this session already reported its sign-in to Studio. Reset by
  // signOut so the next sign-in reports again.
  const loginRecordedRef = useRef(false);

  /**
   * AIVA-7296: report the sign-in to Studio (POST /api/me/login), which stamps
   * `Users.LastLoginAtUtc` and writes the `user.login` audit row.
   *
   * Called ONLY from the paths that establish a session — the mount-time silent
   * sign-in, the Sign-in button, and the interactive dialog — never from a token
   * refresh (`doRefresh` / `refreshGraphToken` / `refreshSession` call
   * `applyTokens` directly). A refresh isn't a new sign-in, and reporting one
   * every ~50 min would bury the audit log. `loginRecordedRef` additionally
   * collapses the case where two sign-in paths race on the same session.
   *
   * A session rehydrated from sessionStorage on pane reopen doesn't report either —
   * it never mints tokens. That's fine for the retention clock: the add-in's
   * inactivity timeout (AIVA-5596; 8 hours at most) forces a genuine sign-in well inside the
   * deactivation threshold, so an in-use account keeps its stamp fresh.
   *
   * The bearer is the backend (API-audience) token, matching the add-in's other
   * WebPortal call (fetchOutlookIntegrationSettings), and falls back to the
   * Graph token exactly as that one does.
   */
  const recordLogin = useCallback((backendToken: string | null) => {
    if (loginRecordedRef.current || !backendToken) return;
    loginRecordedRef.current = true;
    // Fire-and-forget: recordStudioLogin never rejects.
    void recordStudioLogin(backendToken);
  }, []);

  /**
   * `applyTokens` for the sign-in paths: same token bookkeeping, plus the
   * one-per-session Studio login record.
   */
  const applySignInTokens = useCallback(
    (
      accessToken: string,
      newIdToken: string | null,
      newApiToken: string | null,
      expiresAt?: number | null,
    ) => {
      applyTokens(accessToken, newIdToken, newApiToken, expiresAt);
      recordLogin(newApiToken ?? accessToken);
    },
    [applyTokens, recordLogin],
  );

  // Auto-sign-in on mount. A reopened pane starts with empty sessionStorage but
  // the session cookie survives in the browser, so one cookie refresh silently
  // restores the session — provided the CSRF secret is still present. A dead
  // session just leaves the user on the sign-in screen. This replaced an MSAL
  // ssoSilent/acquireTokenSilent pair that spent a browser-held refresh token.
  useEffect(() => {
    if (!authReady || graphToken) return;
    if (!loadCsrfToken()) return;
    void (async () => {
      const result = await refreshViaBff(bffChannel, lastApiTokenRef.current);
      if (result === "interaction_required") {
        // The cookie is gone or dead — the stored CSRF secret is orphaned.
        clearCsrfToken();
        return;
      }
      if (result) {
        applySignInTokens(result.accessToken, result.idToken, result.apiToken, result.expiresAt);
      }
    })();
    // graphToken is deliberately omitted from the deps: this is a mount-time
    // restore, not a reactive refresher — signOut must not re-trigger it.
  }, [authReady, bffChannel, applySignInTokens]);

  // Feed the live token into the telemetry pipeline. The telemetry client is
  // constructed at boot (outside React) with a placeholder token, so we push
  // the current bearer into its module-level holder here. We use the id-token
  // — the same token the addin-api call uses as its bearer — falling back to
  // the Graph token.
  useEffect(() => {
    setTelemetryAuthToken(idToken ?? graphToken);
  }, [idToken, graphToken]);

  // Interactive sign-in — the Office dialog, for every provider and every host.
  // There is no silent tier to try first: the task pane holds no token client,
  // so the dialog is what obtains the authorization code the backend exchanges
  // (V-08). It is also the only reliable cross-host method — both Outlook (the
  // broker iframe, brk-*://) and desktop Word/PowerPoint (embedded WebView)
  // block acquireTokenPopup().
  const signIn = useCallback((): Promise<void> => {
    signInViaDialog(applySignInTokens);
    return Promise.resolve();
  }, [applySignInTokens]);

  /** Raise the idle prompt for a deadline the heartbeat has already computed. */
  const openIdleWarning = useCallback((deadline: number) => {
    idleWarningRef.current = true;
    // Every beat while the prompt is up re-raises the same deadline (nothing
    // moves the activity stamp then), and React bails out of an identical set —
    // so re-raising costs no render.
    setIdleWarningDeadline(deadline);
  }, []);

  /** Take the idle prompt back down (answered, or activity moved the deadline). */
  const closeIdleWarning = useCallback(() => {
    idleWarningRef.current = false;
    setIdleWarningDeadline(null);
  }, []);

  const signOut = useCallback(() => {
    closeIdleWarning();
    // Every sign-out clears the reason first and the caller that has one records
    // it afterwards (see `signOutForInactivity`), so an idle timeout can never
    // leave its explanation behind to mislabel the *next* sign-out — a user who
    // signs out by hand an hour later must not be told they timed out.
    setSignedOutForInactivity(false);
    // Drop the CSRF pairing and ask the backend to clear the session cookie.
    // Both best-effort: the flow is stateless server-side, so local sign-out
    // is authoritative; the cookie clear just tidies the browser. The CSRF
    // secret is read BEFORE clearing — the backend requires it to clear a
    // live cookie (forged cross-site logout protection).
    const csrf = loadCsrfToken();
    clearCsrfToken();
    void bffLogout(csrf, lastApiTokenRef.current);
    clearPersistedTokens();
    // A different persona may sign in next in this same task pane session —
    // its skill list must not be served from the previous user's cache (AIVA-7670).
    invalidateCommandCache();
    signedInRef.current = false;
    // The next sign-in is a new session and reports its own login.
    loginRecordedRef.current = false;
    expiresAtRef.current = null;
    lastIdTokenRef.current = null;
    lastGraphTokenRef.current = null;
    lastApiTokenRef.current = null;
    // Drop the coalescing cache so a refresh requested after sign-out can't
    // short-circuit to a now-dead token instead of failing closed.
    lastRefreshTokenRef.current = null;
    lastRefreshAtRef.current = 0;
    // Detach any in-flight dialog refresh so a post-sign-out caller starts fresh
    // (fails closed via the signedInRef check) rather than awaiting a promise that
    // may still resolve to the now-dead session's token.
    sessionRefreshInFlightRef.current = null;
    setGraphToken(null);
    setIdToken(null);
    setApiToken(null);
  }, [closeIdleWarning]);

  /**
   * Sign out because the inactivity timeout lapsed. Identical to `signOut`
   * except that it records *why*, so the sign-in page can explain the session
   * ended on its own instead of leaving the user to guess.
   */
  const signOutForInactivity = useCallback(() => {
    signOut();
    setSignedOutForInactivity(true);
  }, [signOut]);

  // The one refresh path: redeem the session cookie for a fresh set of tokens.
  // Invisible — no window, no flash — so every trigger uses it, foreground and
  // background alike. `userInitiated` decides only what happens to a DEAD
  // session: on a genuine interaction_required we sign out so the user
  // re-authenticates, but a background caller (the proactive timer, the
  // focus/visibility recovery, the heartbeat) must never eject the user, so it
  // passes false and the miss is silent.
  const refreshSession = useCallback(async (userInitiated: boolean): Promise<string | null> => {
    // AIVA-5861: a dialog refresh is already in flight. AWAIT it and hand back the
    // token it mints rather than returning the not-yet-populated
    // lastRefreshTokenRef (null). This is the crux of the false-expiry fix: the
    // chat (api-audience) refresh and a Graph-audience refresh use different
    // refreshers that both funnel here, and on dialog-sign-in hosts (classic
    // Outlook, desktop Word/PowerPoint) EVERY refresh goes through this tier. When
    // one arrives while another's dialog is still open, it must share that result
    // — not inherit a null that surfaces as a bogus "sign-in has expired" 401 on
    // the request that raced the refresh. Sharing the promise also preserves the
    // single-popup invariant (AIVA-6131): no second dialog can open.
    if (sessionRefreshInFlightRef.current) {
      return sessionRefreshInFlightRef.current;
    }
    // AIVA-5972: a dialog refresh that COMPLETED within the window minted a token
    // (or transiently failed → null). Reuse that outcome without opening a second
    // dialog — collapsing a post-idle 401 burst into a single popup. Failed → null
    // here is the AIVA-6131 contract: one message, not a second/third popup.
    //
    // There is deliberately no attempt-window here. That guard existed to avoid
    // a second visible popup; the cookie fetch is invisible and cheap, and
    // stamping a window would hand a transient failure's null outcome to a
    // reactive user retry that would have succeeded. Coalescing is left to the
    // in-flight sharing above plus refreshTokens' own success-only window.
    //
    // AIVA-6131: a sibling caller may have passed doRefresh's signedInRef check
    // before a concurrent dead-session sign-out flipped it. Re-check here (after
    // that sign-out could have run) so we never pop a dialog for an already
    // signed-out session — the second popup the user saw after the expiry.
    if (!signedInRef.current) {
      return null;
    }
    // Wrap the whole round-trip in one promise stored on the ref, so every
    // concurrent caller shares it (see the in-flight guard above). Cleared in
    // `finally` once the refresh settles, so the next refresh starts clean.
    const run = (async (): Promise<string | null> => {
      // Only meaningful where Graph is a distinct audience (Entra); off-Entra
      // the fresh API token is the better Graph-slot value.
      const staleGraphFallback = capabilities.graph ? lastGraphTokenRef.current : null;
      const result: RefreshResult =
        await refreshViaBff(bffChannel, lastApiTokenRef.current, staleGraphFallback);
      if (result === "interaction_required") {
        // A real user action hit a dead session — sign out so they re-authenticate.
        if (userInitiated) signOut();
        return null;
      }
      if (!result) return null; // open / parse / transient failure — keep the session
      applyTokens(result.accessToken, result.idToken, result.apiToken, result.expiresAt);
      // AIVA-5972: seed the coalescing cache so the rest of a post-idle 401
      // burst reuses this token instead of each request firing its own refresh.
      const backendToken = result.apiToken ?? result.accessToken;
      lastRefreshTokenRef.current = backendToken;
      lastRefreshAtRef.current = Date.now();
      return backendToken;
    })().finally(() => {
      sessionRefreshInFlightRef.current = null;
    });
    sessionRefreshInFlightRef.current = run;
    return run;
  }, [applyTokens, signOut, bffChannel, capabilities.graph]);

  // Silent token refresh — one path, the cookie-credentialed fetch. It is
  // invisible (no window can flash), so background triggers use it as freely as
  // user actions. Sign-out-on-dead-session stays reserved for the foreground:
  // `foreground=false` marks a background caller (heartbeat, telemetry
  // refresher, focus recovery), and a background miss must never eject the
  // user — hence the combined gate, which preserves the invariant that only
  // user-visible attempts can sign out.
  const doRefresh = useCallback((foreground: boolean, userInitiated: boolean): Promise<string | null> => {
    // Signed out — never refresh. A reactive 401 from an in-flight request, a
    // queued background timer, or a closing dialog's focus event can all land
    // here after signOut; the user must re-authenticate via the interactive
    // Sign-in button, which does not go through this path.
    if (!signedInRef.current) return Promise.resolve(null);
    return refreshSession(foreground && userInitiated);
  }, [refreshSession]);

  // `userInitiated` defaults true so every reactive caller (fetchWithRefresh on a
  // 401, refreshIdToken / refreshGraphToken) keeps the sign-out-on-dead-session
  // behaviour. The background effects below pass false so a miss never ejects the
  // user.
  const refreshTokens = useCallback((foreground = true, userInitiated = true): Promise<string | null> => {
    // AIVA-5972: coalesce a post-idle burst of reactive refreshes. If a refresh
    // (or sign-in) landed a fresh token within the window, reuse it rather than
    // opening another Office refresh dialog — collapsing the access-guard
    // preflight + fetchWithRefresh retry + chat outer-retry 401 cascade into a
    // single dialog on desktop dialog-sign-in hosts. The cached token is always
    // freshly minted (see applyTokens) so it is safe to hand back here.
    if (
      lastRefreshTokenRef.current &&
      Date.now() - lastRefreshAtRef.current < REFRESH_COALESCE_WINDOW_MS
    ) {
      return Promise.resolve(lastRefreshTokenRef.current);
    }
    const startRefresh = (): Promise<string | null> => {
      const run = doRefresh(foreground, userInitiated).finally(() => {
        refreshInFlightRef.current = null;
        refreshInFlightForegroundRef.current = false;
      });
      refreshInFlightRef.current = run;
      refreshInFlightForegroundRef.current = foreground;
      return run;
    };
    const inFlight = refreshInFlightRef.current;
    // Reuse the in-flight refresh only when it is at least as capable as this
    // request: it already allows the dialog, or this caller doesn't need it.
    if (inFlight && (refreshInFlightForegroundRef.current || !foreground)) return inFlight;
    // A silent-only refresh is in flight but this caller may open the dialog.
    // Wait for it: if it recovered a token, use that; otherwise escalate to a
    // dialog-capable refresh rather than inheriting its null (AIVA-5861). If a
    // sibling caller already started the dialog refresh while we awaited, reuse
    // it so we never open a second dialog.
    if (inFlight) {
      return inFlight.then((token) => {
        if (token) return token;
        const next = refreshInFlightRef.current;
        if (next && refreshInFlightForegroundRef.current) return next;
        return startRefresh();
      });
    }
    return startRefresh();
  }, [doRefresh]);

  /**
   * "Keep me signed in" from the idle prompt. Restarting the activity clock is
   * what actually saves the session — every idle gate (the heartbeat, and the
   * reopen gate on the persisted tokens) reads that one stamp.
   *
   * The refresh is silent-only and not user-initiated on purpose: answering a
   * prompt must never flash an Office auth dialog, and a refresh that can't
   * recover a token here must not sign the user out — they just told us they are
   * still working. A session idle for the default ~2h is normally past its refresh
   * buffer, so this gets the bearer back ahead of their next action; where it
   * can't (dialog-sign-in hosts), the reactive 401 retry still does.
   */
  const extendSession = useCallback(() => {
    recordActivity();
    closeIdleWarning();
    refreshTokens(false, false).catch(() => undefined);
  }, [closeIdleWarning, refreshTokens]);

  // Fresh id token for callers whose bearer is the id token. Piggybacks on
  // `refreshTokens` (so it shares the single in-flight round-trip and its
  // dedup), then reads the id token `applyTokens` set synchronously during that
  // refresh. Returns null whenever the underlying refresh did (transient
  // failure / dead session).
  const refreshIdToken = useCallback(
    (foreground = true): Promise<string | null> =>
      refreshTokens(foreground).then((backend) =>
        backend == null ? null : lastIdTokenRef.current,
      ),
    [refreshTokens],
  );

  // Fresh Graph access token for direct Graph calls (email / calendar / files).
  //
  // Unlike refreshIdToken, this does NOT piggyback on refreshTokens. That path
  // is tuned for the backend (api) token: its coalescing window (see
  // refreshTokens) can hand back a recently-minted *backend* token without
  // running doRefresh, leaving the expired Graph token in lastGraphTokenRef —
  // so a post-idle Graph 401 retry re-sent the stale token and 401'd again in a
  // loop. Going through refreshSession instead mints every audience together:
  // graphToken arrives in the same response body, and applyTokens has already
  // stored it by the time the call resolves. It is read from auth state rather
  // than MSAL because the task pane runs no token client (V-08).
  //
  // `foreground` keeps its meaning as the silent-caller marker: a background
  // miss returns null and never signs the user out.
  const refreshGraphToken = useCallback(
    async (foreground = true): Promise<string | null> => {
      if (!signedInRef.current) return null;
      const backend = await refreshSession(foreground);
      return backend == null ? null : lastGraphTokenRef.current;
    },
    [refreshSession],
  );

  // Pre-flight for a user-initiated backend call: return a backend bearer we
  // already know is valid, refreshing FIRST when the tracked expiry says it is
  // stale — instead of sending a token we know is expired and reacting to the
  // 401. This is the root-cause fix for AIVA-5861: on dialog-sign-in hosts
  // (classic Outlook, desktop Word/PowerPoint) the background timers refresh
  // silent-only and no-op, so after idle the persisted token is already past
  // expiry. Sending it either eats a 401 round-trip OR passes APIM but fails the
  // backend's OBO exchange on the stale assertion, surfacing "Your sign-in has
  // expired" mid-turn with no 401 for the client to retry. Refreshing up-front
  // (dialog allowed — this runs on a real user action, so a popup here is
  // expected, not a background flash) sends a fresh token so the turn succeeds on
  // the first try. Falls back to the current bearer when the token is still
  // fresh, or when a refresh can't complete (the reactive 401 path then covers a
  // token that lapses between here and the request).
  const ensureFreshToken = useCallback(async (): Promise<string | null> => {
    if (!signedInRef.current) return null;
    const currentBackend = lastApiTokenRef.current ?? lastGraphTokenRef.current;
    const expiresAt = expiresAtRef.current;
    const stale = expiresAt == null || expiresAt - Date.now() < REFRESH_BUFFER_MS;
    if (!stale) return currentBackend;
    // Stale (or unknown expiry) — refresh now. userInitiated=true so a genuinely
    // dead session signs out cleanly; foreground=true so dialog-sign-in hosts can
    // actually renew. On failure fall back to the current bearer so the caller's
    // reactive path still runs rather than being handed a premature null.
    const refreshed = await refreshTokens(true, true);
    return refreshed ?? currentBackend;
  }, [refreshTokens]);

  // The telemetry transport is built at boot (outside React) and POSTs the
  // idToken as its bearer with no 401 retry of its own. Hand it a refresher so a
  // batch that races an expired idToken re-acquires one and retries instead of
  // dropping events. `foreground: false` — telemetry is background, so it must
  // never pop an auth dialog (option A).
  useEffect(() => {
    setTelemetryTokenRefresher(() => refreshIdToken(false));
    return () => setTelemetryTokenRefresher(null);
  }, [refreshIdToken]);

  // Proactive refresh: re-acquire ~5 min before expiry. Re-arms whenever a new
  // token lands (graphToken/apiToken change), so it self-perpetuates for the
  // life of the session. A session rehydrated from sessionStorage near (or past)
  // its expiry arms with delay 0, so this also up-front-refreshes on mount.
  //
  // This is a BACKGROUND trigger, so it refreshes SILENT-ONLY (foreground =
  // false): on NAA / cached-account hosts Tier A renews the token invisibly; on
  // dialog-sign-in hosts (classic Outlook, desktop Word/PowerPoint) it is a no-op
  // and the token is recovered by the reactive 401 path on the user's next action.
  // Never opening the visible dialog from here is what stops the "refresh popup
  // appears while the user is idle" loop — the dialog is reserved for user actions.
  useEffect(() => {
    if (!signedIn || expiresAtRef.current == null) return;
    const delay = Math.max(0, expiresAtRef.current - Date.now() - REFRESH_BUFFER_MS);
    const id = setTimeout(() => {
      refreshTokens(false, false).catch(() => undefined);
    }, delay);
    return () => clearTimeout(id);
  }, [signedIn, graphToken, apiToken, refreshTokens]);

  // AIVA-6685 (V-08): purge any legacy tokens a pre-upgrade session left behind
  // in localStorage, so the fix removes existing exposure rather than only
  // stopping future writes. Runs once on mount; a no-op after the first load.
  //
  // This replaces the former cross-window sign-in/out sync (AIVA-5847), which
  // relied on `localStorage` being shared across add-in windows and firing the
  // `storage` event. Token storage is now sessionStorage (per-window, no
  // cross-window `storage` events), so that sync can no longer function — each
  // add-in window now manages its own session independently.
  useEffect(() => {
    purgeLegacyLocalStorageTokens();
    // Cutover: also purge the caches the removed browser token clients left
    // behind (dialog MSAL, oidc-client-ts) — the refresh tokens they held are
    // exactly what this flow takes out of the browser. Safe no-op once gone.
    purgeLegacyIdpCaches();
  }, []);

  // Idle/sleep recovery: setTimeout is unreliable across machine sleep, so when
  // the task pane regains focus/visibility we refresh up-front if the token is
  // within the buffer (or its expiry is unknown, e.g. a restored session). Like
  // the proactive timer this is background/silent-only (foreground = false), so
  // the closing-dialog focus event a user-initiated refresh fires can never
  // re-open the dialog — no popup loop, and no cooldown needed.
  useEffect(() => {
    if (!signedIn) return;
    const maybeRefresh = () => {
      const expiresAt = expiresAtRef.current;
      if (expiresAt == null || expiresAt - Date.now() < REFRESH_BUFFER_MS) {
        refreshTokens(false, false).catch(() => undefined);
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") maybeRefresh();
    };
    window.addEventListener("focus", maybeRefresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [signedIn, refreshTokens]);

  // Heartbeat backstop: the single proactive `setTimeout` above is throttled or
  // suspended while the host app is backgrounded/asleep, and the focus/visibility
  // events often never fire in embedded Office WebViews — so after an hour idle
  // neither path refreshes and the next API call goes out with a dead bearer.
  // This interval re-checks expiry every minute and refreshes once the token is
  // within the buffer (or its expiry is unknown), catching the window as soon as
  // the webview gets execution time again. `foreground` is false so it only
  // refreshes where it can do so invisibly (NAA / new Outlook); on dialog-sign-in
  // hosts the token is recovered by the reactive 401 retry on the user's next
  // action, never a background popup. `userInitiated` is false so a miss keeps
  // the session. The shared `refreshInFlightRef` de-dups it against the timer,
  // focus handler, and any reactive 401 onto a single round-trip.
  useEffect(() => {
    if (!signedIn) return;
    const id = setInterval(() => {
      // AIVA-5596: a live session idle past the inactivity timeout is signed out
      // here, before any refresh — this stops the refresh below from renewing a
      // session the user has abandoned. AIVA-7702 moved the ordinary case ahead
      // of it: the prompt raised below ends the session on the exact second its
      // countdown runs out (`endIdleSession`), so this check is what still fires
      // when no prompt is mounted or this interval was starved while the host
      // slept. Both report the same reason, so the sign-in page reads the same
      // either way.
      if (isInactivityExpired(idleTimeoutMs)) {
        signOutForInactivity();
        return;
      }
      // AIVA-7702: raise the expiry prompt as the deadline comes into view. The
      // lead time is wider than this beat, so a beat always lands inside the
      // window — the user gets at least most of a minute of notice, and the
      // prompt counts the remainder down itself.
      const idleDeadline = idleDeadlineAt(idleTimeoutMs);
      if (idleDeadline != null && idleDeadline - Date.now() <= INACTIVITY_WARNING_LEAD_MS) {
        openIdleWarning(idleDeadline);
      } else if (idleWarningRef.current) {
        // Activity pushed the deadline back out of view — `extendSession` already
        // closes the prompt itself, so this is the belt-and-braces path.
        closeIdleWarning();
      }
      const expiresAt = expiresAtRef.current;
      if (expiresAt == null || expiresAt - Date.now() < REFRESH_BUFFER_MS) {
        refreshTokens(false, false).catch(() => undefined);
      }
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [signedIn, idleTimeoutMs, refreshTokens, signOutForInactivity, openIdleWarning, closeIdleWarning]);

  // AIVA-5596: track genuine user activity to drive the inactivity timeout. The
  // stamp is advanced ONLY here (never by a token refresh), so the heartbeat and
  // the reopen gate can tell a busy session from an abandoned one. Writes are
  // throttled to ACTIVITY_WRITE_THROTTLE_MS so the high-frequency events don't
  // hammer localStorage. Seeding on entry treats opening/using the pane as
  // activity and stamps a freshly signed-in session that has no prior key.
  //
  // Limitation: a task pane only sees DOM events inside its own iframe — typing
  // in the Word document body is not observable here, so "activity" means
  // interaction with the add-in pane.
  useEffect(() => {
    if (!signedIn) return;
    let lastWrite = 0;
    const onActivity = () => {
      // While the expiry prompt is up, passive events must not answer it on the
      // user's behalf. The prompt asks a question ("still working?") and a stray
      // mousemove, or the host handing focus back to the pane, is not an answer —
      // letting either restart the clock would make the prompt vanish before it
      // was read, which is barely better than the silent logout it replaced. From
      // the moment it opens, only its own buttons (`extendSession`) count.
      if (idleWarningRef.current) return;
      const now = Date.now();
      if (now - lastWrite < ACTIVITY_WRITE_THROTTLE_MS) return;
      lastWrite = now;
      recordActivity();
    };
    // Seed immediately so the throttle gate above is anchored and a new session
    // has a fresh stamp.
    lastWrite = Date.now();
    recordActivity();
    const events = ["mousemove", "keydown", "click", "focus"] as const;
    for (const evt of events) globalThis.addEventListener(evt, onActivity);
    document.addEventListener("visibilitychange", onActivity);
    return () => {
      for (const evt of events) globalThis.removeEventListener(evt, onActivity);
      document.removeEventListener("visibilitychange", onActivity);
    };
  }, [signedIn]);

  return (
    <AuthStateCtx.Provider value={useMemo(() => ({ graphToken, idToken, apiToken, signedIn, ready: authReady, signIn, signOut, refreshTokens, refreshIdToken, refreshGraphToken, ensureFreshToken, idleWarningDeadline, extendSession, endIdleSession: signOutForInactivity, signedOutForInactivity, idleTimeoutMinutes: idleTimeoutMs / (60 * 1000) }), [graphToken, idToken, apiToken, signedIn, authReady, signIn, signOut, refreshTokens, refreshIdToken, refreshGraphToken, ensureFreshToken, idleWarningDeadline, extendSession, signOutForInactivity, signedOutForInactivity, idleTimeoutMs])}>
      {children}
    </AuthStateCtx.Provider>
  );
}
